import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import AdminNavbar from '../../components/admin/AdminNavbar';
import './AdminCommon.css';

const DEFAULT_MESA = [
  { nombre: 'JOHANA ELIZABETH CUATIN GALINDRES', cargo: 'Presidenta', periodo: 'MESA DIRECTIVA PERIODO 2025' },
  { nombre: 'JERCY BLADIMIR AZA', cargo: 'Primer Vicepresidente', periodo: 'MESA DIRECTIVA PERIODO 2025' },
  { nombre: 'ROMMEL ALVEIRO CUASTUMAL RODRIGUEZ', cargo: 'Segundo Vicepresidente Ad Hoc', periodo: 'MESA DIRECTIVA PERIODO 2025' },
  { nombre: 'ANGELA CRISTINA HERNANDEZ REINA', cargo: 'Secretaria General', periodo: 'Vigencia 2025' }
];

const DEFAULT_CRONOGRAMA = [
  { trimestre: 'Q1', meses: 'Ene - Mar', actividades: 'Inicio del plan, diagnostico inicial', estado: 'completado' },
  { trimestre: 'Q2', meses: 'Abr - Jun', actividades: 'Implementacion de herramientas, capacitaciones', estado: 'en-progreso' },
  { trimestre: 'Q3', meses: 'Jul - Sep', actividades: 'Seguimiento y evaluacion, ajustes', estado: 'pendiente' },
  { trimestre: 'Q4', meses: 'Oct - Dic', actividades: 'Cierre del ano, rendicion de cuentas', estado: 'pendiente' }
];

const parseJson = (value, fallback) => {
  if (!value || typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    return fallback;
  }
};

const AdminPlanAccion = () => {
  const queryClient = useQueryClient();
  const { data: config = {}, isLoading } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      const response = await api.get('/configuracion');
      return response.data;
    }
  });

  const mesaInicial = useMemo(
    () => parseJson(config.plan_accion_mesa_json, DEFAULT_MESA),
    [config.plan_accion_mesa_json]
  );

  const cronogramaInicial = useMemo(
    () => parseJson(config.plan_accion_cronograma_json, DEFAULT_CRONOGRAMA),
    [config.plan_accion_cronograma_json]
  );

  const [formData, setFormData] = useState({
    titulo: '',
    subtitulo: '',
    fecha: '',
    resolucion: '',
    vigencia: ''
  });
  const [mesaDirectiva, setMesaDirectiva] = useState(DEFAULT_MESA);
  const [cronograma, setCronograma] = useState(DEFAULT_CRONOGRAMA);

  React.useEffect(() => {
    setFormData({
      titulo: config.plan_accion_titulo || 'Plan de Accion 2025',
      subtitulo: config.plan_accion_subtitulo || 'Concejo Municipal de Guachucal - Narino',
      fecha: config.plan_accion_fecha || 'Enero 30 de 2025',
      resolucion: config.plan_accion_resolucion || 'Resolucion No. 008 de 2025',
      vigencia: config.plan_accion_vigencia || '2025'
    });
    setMesaDirectiva(mesaInicial);
    setCronograma(cronogramaInicial);
  }, [config, mesaInicial, cronogramaInicial]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = [
        { clave: 'plan_accion_titulo', valor: formData.titulo },
        { clave: 'plan_accion_subtitulo', valor: formData.subtitulo },
        { clave: 'plan_accion_fecha', valor: formData.fecha },
        { clave: 'plan_accion_resolucion', valor: formData.resolucion },
        { clave: 'plan_accion_vigencia', valor: formData.vigencia },
        { clave: 'plan_accion_mesa_json', valor: JSON.stringify(mesaDirectiva) },
        { clave: 'plan_accion_cronograma_json', valor: JSON.stringify(cronograma) }
      ];
      await Promise.all(payload.map(item => api.put(`/configuracion/${item.clave}`, { valor: item.valor })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['configuracion']);
      alert('Plan de accion actualizado exitosamente');
    },
    onError: (error) => {
      alert('Error actualizando plan de accion: ' + (error.response?.data?.error || error.message));
    }
  });

  const updateMesa = (index, key, value) => {
    setMesaDirectiva(prev => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const addMesa = () => {
    setMesaDirectiva(prev => [...prev, { nombre: '', cargo: '', periodo: `Vigencia ${formData.vigencia || '2026'}` }]);
  };

  const removeMesa = (index) => {
    setMesaDirectiva(prev => prev.filter((_, i) => i !== index));
  };

  const updateCronograma = (index, key, value) => {
    setCronograma(prev => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const addCronograma = () => {
    setCronograma(prev => [...prev, { trimestre: '', meses: '', actividades: '', estado: 'pendiente' }]);
  };

  const removeCronograma = (index) => {
    setCronograma(prev => prev.filter((_, i) => i !== index));
  };

  if (isLoading) return <div className="loading">Cargando configuracion del plan...</div>;

  return (
    <div className="admin-configuracion">
      <AdminNavbar title="Plan de Accion" />
      <div className="admin-content-wrapper">
        <div className="container">
          <div className="config-section">
            <h2>Datos generales del Plan</h2>
            <div className="config-items">
              <div className="config-item">
                <div className="config-label"><label>Titulo</label></div>
                <div className="config-value"><input className="config-input" value={formData.titulo} onChange={(e) => setFormData(p => ({ ...p, titulo: e.target.value }))} /></div>
              </div>
              <div className="config-item">
                <div className="config-label"><label>Subtitulo</label></div>
                <div className="config-value"><input className="config-input" value={formData.subtitulo} onChange={(e) => setFormData(p => ({ ...p, subtitulo: e.target.value }))} /></div>
              </div>
              <div className="config-item">
                <div className="config-label"><label>Fecha</label></div>
                <div className="config-value"><input className="config-input" value={formData.fecha} onChange={(e) => setFormData(p => ({ ...p, fecha: e.target.value }))} /></div>
              </div>
              <div className="config-item">
                <div className="config-label"><label>Resolucion</label></div>
                <div className="config-value"><input className="config-input" value={formData.resolucion} onChange={(e) => setFormData(p => ({ ...p, resolucion: e.target.value }))} /></div>
              </div>
              <div className="config-item">
                <div className="config-label"><label>Vigencia</label></div>
                <div className="config-value"><input className="config-input" value={formData.vigencia} onChange={(e) => setFormData(p => ({ ...p, vigencia: e.target.value }))} /></div>
              </div>
            </div>
          </div>

          <div className="config-section">
            <h2>Mesa Directiva (editable)</h2>
            {mesaDirectiva.map((item, index) => (
              <div key={`mesa-${index}`} className="config-item" style={{ marginBottom: '1rem' }}>
                <div className="config-label">
                  <label>Miembro {index + 1}</label>
                </div>
                <div className="config-value">
                  <input className="config-input" placeholder="Cargo" value={item.cargo} onChange={(e) => updateMesa(index, 'cargo', e.target.value)} />
                  <input className="config-input" placeholder="Nombre" value={item.nombre} onChange={(e) => updateMesa(index, 'nombre', e.target.value)} style={{ marginTop: '0.5rem' }} />
                  <input className="config-input" placeholder="Periodo" value={item.periodo} onChange={(e) => updateMesa(index, 'periodo', e.target.value)} style={{ marginTop: '0.5rem' }} />
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => removeMesa(index)} style={{ marginTop: '0.5rem' }}>Eliminar</button>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addMesa}>+ Agregar miembro</button>
          </div>

          <div className="config-section">
            <h2>Cronograma</h2>
            {cronograma.map((item, index) => (
              <div key={`crono-${index}`} className="config-item" style={{ marginBottom: '1rem' }}>
                <div className="config-label">
                  <label>Tramo {index + 1}</label>
                </div>
                <div className="config-value">
                  <input className="config-input" placeholder="Trimestre" value={item.trimestre} onChange={(e) => updateCronograma(index, 'trimestre', e.target.value)} />
                  <input className="config-input" placeholder="Meses" value={item.meses} onChange={(e) => updateCronograma(index, 'meses', e.target.value)} style={{ marginTop: '0.5rem' }} />
                  <input className="config-input" placeholder="Actividades" value={item.actividades} onChange={(e) => updateCronograma(index, 'actividades', e.target.value)} style={{ marginTop: '0.5rem' }} />
                  <select className="config-input" value={item.estado} onChange={(e) => updateCronograma(index, 'estado', e.target.value)} style={{ marginTop: '0.5rem' }}>
                    <option value="pendiente">Pendiente</option>
                    <option value="en-progreso">En Progreso</option>
                    <option value="completado">Completado</option>
                  </select>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => removeCronograma(index)} style={{ marginTop: '0.5rem' }}>Eliminar</button>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addCronograma}>+ Agregar tramo</button>
          </div>

          <div className="config-section">
            <button type="button" className="btn btn-primary" onClick={() => mutation.mutate()} disabled={mutation.isLoading}>
              {mutation.isLoading ? 'Guardando...' : 'Guardar configuracion del Plan de Accion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPlanAccion;

